import { t } from 'i18next';
import React from 'react';
import { LocationEvent } from '../../../domain/models/locations/LocationEvent';
import { snakeToCamel } from '@pocapan/shared/utils/TextUtil';

interface EventFeaturesProps {
  eventDetail: LocationEvent;
}

const GIFYTYPES = [
  { key: 'first_come', icon: 'Index pointing up' },
  { key: 'menu', icon: 'Shortcake' },
  { key: 'special_day', icon: 'Tear-off calendar' },
  { key: 'lucky', icon: 'Four leaf clover' },
  { key: 'etc', icon: 'Wrapped gift' }
];

const EventGoodsGiftTypes: React.FC<EventFeaturesProps> = ({ eventDetail }) => {
  return (
    <div className="px-[20px]">
      <div className="flex flex-col py-[10px] gap-[4px]">
        <div className="text-[15px] leading-[18px] font-semibold text-main-light-2 dark:text-main-dark-2">
          {t('OfflineEventDetail.eventGoodsGiftTypesTitle')}
        </div>
        <div className="text-[13px] leading-[18px] font-normal text-main-light-4 dark:text-main-dark-4">
          {t('OfflineEventDetail.eventGoodsGiftTypesDescription')}
        </div>
      </div>
      <div className="pt-[10px] pb-[4px]">
        {GIFYTYPES.map((giftType) => (
          <GiftTypeTextRow
            key={giftType.key}
            eventDetail={eventDetail}
            giftTypeKey={giftType.key}
            iconName={giftType.icon}
          />
        ))}
      </div>
    </div>
  );
};

const formatGiftTypesText = (types: string[]): string =>
  types.length > 0 ? types.map((type) => t(`GoodsGiftBadge.${snakeToCamel(type)}`)).join(', ') : t('GoodsGiftTypes.unavailable');


const getGiftTextAndStatus = (
  giftType: string,
  eventDetail: LocationEvent
): { giftText: string; hasTypes: boolean } => {
  if (giftType === 'lucky') {
    return {
      giftText: formatGiftTypesText(eventDetail.luckyGiftTypes),
      hasTypes: eventDetail.luckyGiftTypes.length > 0
    };
  }

  if (giftType === 'etc') {
    return {
      giftText: formatGiftTypesText(eventDetail.etcGiftTypes),
      hasTypes: eventDetail.etcGiftTypes.length > 0
    };
  }

  return {
    hasTypes: eventDetail.hasGoodsGiftTypes(giftType),
    giftText: eventDetail.hasGoodsGiftTypes(giftType)
      ? t('GoodsGiftTypes.available')
      : t('GoodsGiftTypes.unavailable')
  };
};

interface HasGoodsGiftTypeTextProps {
  eventDetail: LocationEvent;
  giftTypeKey: string;
  iconName: string;
}

const GiftTypeTextRow = ({ eventDetail, giftTypeKey, iconName }: HasGoodsGiftTypeTextProps) => {
  const { giftText, hasTypes } = getGiftTextAndStatus(giftTypeKey, eventDetail);

  const iconPath = `/assets/icons/graphic-3d/3D.${iconName}.png`;

  return (
    <div className="flex flex-row justify-between items-center py-[8px]">
      <div className="flex flex-row items-center">
        <div className="w-[28px] h-[28px] rounded-full bg-separator-light-1 dark:bg-separator-dark-1 flex items-center justify-center">
          <img src={iconPath} alt={`${iconName}_gift_icon`} className="w-[14px] h-[14px]" />
        </div>
        <div className="ml-[8px] text-[14px] font-medium leading-[17px] text-main-light-3 dark:text-main-dark-3">
          {t(`GoodsGiftTypes.${snakeToCamel(giftTypeKey)}`)}
        </div>
      </div>
      <div
        className={
          hasTypes
            ? 'font-semibold text-[13px] text-textPrimary-light dark:text-textPrimary-dark'
            : 'font-medium text-[13px] text-sub-light-1 dark:text-sub-dark-1'
        }
      >
        {giftText}
      </div>
    </div>
  );
};

export default EventGoodsGiftTypes;
